import CloseIcon from "@mui/icons-material/CloseRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import documentEventsApi from "../../api/clients/documentEventsApi";
import DocumentDialogIcon from "../../icons/DocumentDialogIcon";
import { logError } from "../../logging";
import { downloadFileFromUrl } from "../../services/downloadFile";
import DialogButtons from "../DialogButtons";

interface Props {
  url: string;
  fileName: string;
  documentViewSessionId?: string;
  preventDownload?: boolean;
  onClose: () => void;
}

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[FileCannotBePreviewedDialog] ingestDocumentDownloadEvent");
  }
};

const FileCannotBePreviewedDialog = ({ url, fileName, documentViewSessionId, preventDownload, onClose }: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleDownloadClick = async () => {
    await downloadFileFromUrl(url);

    if (documentViewSessionId) {
      await sendDownloadEvent(documentViewSessionId);
    }
  };

  return (
    <Dialog open fullScreen={fullScreen} onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" noWrap>
            Preview
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: "center", p: 3 }}>
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <DocumentDialogIcon sx={{ width: 80, height: 80 }} />
            <Typography variant="h6">{fileName}</Typography>
            <Typography color="textSecondary">
              {preventDownload
                ? "This file cannot be previewed."
                : "This file cannot be previewed. Please download it."}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        {!preventDownload && (
          <Button color="primary" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadClick}>
            Download File
          </Button>
        )}
      </DialogButtons>
    </Dialog>
  );
};

export default FileCannotBePreviewedDialog;
